import { useCallback, useEffect, useState } from 'react';
import { useTrait } from 'koota/react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables } from '../module_bindings';
import { ActiveTokenHash, SessionState, session } from '../world';
import type { SessionStateValue } from '../world';
import {
  loadStore,
  type LocalIdentityStore,
} from './identityStore';
import { DB_NAME, HOST } from './config';

// ---------------------------------------------------------------------------
// Pure session-state hooks
// ---------------------------------------------------------------------------

export function useSessionState(): SessionStateValue {
  const value = useTrait(session, SessionState);
  return value?.value ?? 'LANDING';
}

export function useActiveTokenHash(): string {
  const value = useTrait(session, ActiveTokenHash);
  return value?.value ?? '';
}

export function setSessionState(next: SessionStateValue): void {
  session.set(SessionState, { value: next });
}

export function setActiveTokenHash(next: string): void {
  session.set(ActiveTokenHash, { value: next });
}

// ---------------------------------------------------------------------------
// Local identity store hook (subscribes to a "version" counter so updates
// from CRUD helpers re-render consumers).
// ---------------------------------------------------------------------------

const storeListeners = new Set<() => void>();
function notifyStoreChange() {
  for (const fn of storeListeners) fn();
}

export function useLocalIdentities(): LocalIdentityStore {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick(t => t + 1);
    storeListeners.add(fn);
    return () => {
      storeListeners.delete(fn);
    };
  }, []);
  return loadStore(HOST, DB_NAME);
}

// Wrap any mutating helper with this so consumers re-render after the change.
export function notifyIdentityStoreUpdate(): void {
  notifyStoreChange();
}

// ---------------------------------------------------------------------------
// Hooks that require an active SpacetimeDB connection (only valid inside the
// SpacetimeDBProvider mounted by the session guard).
// ---------------------------------------------------------------------------

export function useIsConnected(): boolean {
  const conn = useSpacetimeDB();
  return conn?.isActive ?? false;
}

export type CurrentUser = {
  identity: string;
  userId: bigint;
  name: string | undefined;
  isAdmin: boolean;
  isBanned: boolean;
};

/**
 * Return the User row matching the live connection's identity.
 *
 * Three-valued so callers can distinguish "tables loading" from "loaded,
 * no user":
 *   - `undefined` → tables haven't loaded yet (don't act on this)
 *   - `null`      → tables loaded, no User row for this identity
 *   - `CurrentUser` → User exists
 *
 * Display callers (`me?.name`) work transparently across all three.
 * Decision-making callers (e.g. ConnectedShell's auto-enter) must
 * branch explicitly to avoid firing reducers before the load completes
 * (which would race-create duplicate User rows for returning identities).
 */
export function useCurrentUser(): CurrentUser | null | undefined {
  const conn = useSpacetimeDB();
  const myIdentity = conn?.identity?.toHexString() ?? null;
  // NOTE: useTable returns [rows, isReady] — the boolean is `true` once
  // the initial subscription snapshot has applied. It is NOT `isLoading`
  // (which would be its inverse). Treating it backwards causes
  // `useCurrentUser()` to return `null` during the initial-load window,
  // which then makes `ConnectedShell` auto-fire `enterWorld()` for an
  // identity that already has a User row. Don't.
  const [users, isReady] = useTable(tables.user);

  if (!isReady) return undefined;
  if (!myIdentity) return undefined;
  for (const u of users) {
    if (u.identity.toHexString() === myIdentity) {
      return {
        identity: myIdentity,
        userId: u.userId,
        name: u.name ?? undefined,
        isAdmin: u.isAdmin,
        isBanned: u.isBanned,
      };
    }
  }
  return null;
}

export function useIsAdmin(): boolean {
  const user = useCurrentUser();
  return user?.isAdmin ?? false;
}

// ---------------------------------------------------------------------------
// Simple URL-pathname router (no router lib for two routes; compose later if
// the surface grows).
// ---------------------------------------------------------------------------

export function usePath(): string {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

export function navigate(to: string): void {
  if (window.location.pathname === to) return;
  window.history.pushState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useNavigate(): (to: string) => void {
  return useCallback(navigate, []);
}
